import React, { useEffect, useState } from 'react';
import { getData } from '../../../../services/apiService';

const ExamTimetable = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [examData, setExamData] = useState(null);

    useEffect(() => {
        const fetchExamTimetable = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await getData(`/HallTicket/GetStudentExamTimetable?rollNo=24100001`);
                
                if (response?.success && response?.data) {
                    setExamData(response.data);
                } else {
                    setError(response?.message || 'Failed to fetch exam timetable');
                }
            } catch (error) {
                console.error('Error fetching exam timetable:', error);
                setError(error.message || 'An error occurred while fetching exam timetable');
            } finally {
                setLoading(false);
            }
        };

        fetchExamTimetable();
    }, []);

    // Loading State
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4 md:p-8 font-sans">
                <div className="max-w-full mx-auto h-[calc(100vh-2rem)] flex flex-col">
                    <div className="animate-pulse">
                        <div className="relative overflow-hidden rounded-2xl shadow-xl mb-4 bg-gradient-to-r from-[#0b3b45] via-[#0d4f5c] to-[#1f7e8f] h-48"></div>
                        <div className="space-y-4">
                            <div className="h-20 bg-gray-200 rounded-2xl"></div>
                            <div className="h-20 bg-gray-200 rounded-2xl"></div>
                            <div className="h-20 bg-gray-200 rounded-2xl"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error State
    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4 md:p-8 font-sans">
                <div className="max-w-full mx-auto">
                    <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded-lg">
                        <p className="font-bold">Error</p>
                        <p>{error}</p>
                    </div>
                    <button 
                        onClick={() => window.location.reload()} 
                        className="bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] hover:from-[#1a7a8a] hover:to-[#0d4f5c] text-white font-bold py-2 px-4 rounded transition-all duration-300"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    // No Data State
    if (!examData || !examData.examSchedule || examData.examSchedule.length === 0) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4 md:p-8 font-sans">
                <div className="max-w-full mx-auto">
                    <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded-lg">
                        <p className="font-bold">No Exams Found</p>
                        <p>No exam schedule available for this student.</p>
                    </div>
                </div>
            </div>
        );
    }

    const { 
        studentName, 
        rollNo, 
        institutionName, 
        degree, 
        course, 
        batch, 
        procYear, 
        procSem, 
        examSchedule, 
        semester: CurrentSemester 
    } = examData;

    // Add isArrear property based on examType
    const examScheduleWithArrear = examSchedule.map(exam => ({
        ...exam,
        isArrear: exam.examType === 'A' || exam.examType === 'Arrear' || exam.examType === 'Arrear Exam'
    }));

    // Sort exams: Current semester first, then other semesters
    // Within each semester: Regular first, then Arrear
    const sortedExams = [...examScheduleWithArrear].sort((a, b) => {
        // Extract semester from subject code or use default
        // Since API doesn't provide semester in examSchedule, we'll group all as current semester
        // If you have multiple semesters, you'd need to add logic here
        
        // Regular first, then Arrear
        if (a.isArrear !== b.isArrear) {
            return a.isArrear ? 1 : -1; // false (regular) comes first
        }

        // If both same type, sort by date
        return new Date(a.examDate) - new Date(b.examDate);
    });

    // Get unique semesters from sorted exams
    // Since API doesn't provide semester in examSchedule, we'll use the semester from main data
    const semesters = [CurrentSemester || 1];

    const formatDate = (dateStr) => {
        if (!dateStr) return 'TBD';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const getSessionLabel = (session) => {
        if (session === 'A') return 'Afternoon';
        if (session === 'F') return 'Forenoon';
        if (session === 'FN') return 'Forenoon';
        if (session === 'AN') return 'Afternoon';
        return '';
    };

    const getTimeDisplay = (exam) => {
        if (exam.afterNoon) return exam.afterNoon;
        if (exam.foreNoon) return exam.foreNoon;
        return 'TBD';
    };

    const regularCount = examScheduleWithArrear.filter(e => !e.isArrear).length;
    const arrearCount = examScheduleWithArrear.filter(e => e.isArrear).length;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4 md:p-8 font-sans">
            <div className="max-w-full mx-auto h-[calc(100vh-2rem)] flex flex-col">

                {/* ========== COMPACT HEADER (fixed) ========== */}
                <div className="relative overflow-hidden rounded-2xl shadow-xl mb-4 bg-gradient-to-r from-[#0b3b45] via-[#0d4f5c] to-[#1f7e8f] flex-shrink-0">
                    <div className="absolute top-0 right-0 w-56 h-56 bg-teal-300/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-300/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4"></div>

                    <div className="relative px-5 py-4 md:px-7 md:py-5">
                        {/* Top row */}
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div>
                                <div className="flex items-center gap-2.5">
                                    <div className="bg-white/20 backdrop-blur p-2 rounded-xl border border-white/10 shadow-lg">
                                        <i className="fas fa-calendar-check text-xl text-teal-200"></i>
                                    </div>
                                    <div>
                                        <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight drop-shadow-sm">
                                            Exam Timetable
                                        </h1>
                                        <p className="text-teal-100/80 text-xs md:text-sm flex items-center gap-1.5 mt-0">
                                            <i className="fas fa-graduation-cap text-[10px]"></i> {degree} • {course}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <div className="bg-white/20 backdrop-blur-sm rounded-xl px-3 py-1.5 text-white border border-white/10 shadow-sm flex items-center gap-1.5">
                                    <i className="fas fa-calendar-alt text-teal-200 text-xs"></i>
                                    <div>
                                        <span className="text-[8px] uppercase tracking-widest opacity-80 block leading-3">Batch</span>
                                        <span className="font-bold text-sm leading-4">{batch}</span>
                                    </div>
                                </div>
                                <div className="bg-white/20 backdrop-blur-sm rounded-xl px-3 py-1.5 text-white border border-white/10 shadow-sm flex items-center gap-1.5">
                                    <i className="fas fa-layer-group text-teal-200 text-xs"></i>
                                    <div>
                                        <span className="text-[8px] uppercase tracking-widest opacity-80 block leading-3">Current Sem</span>
                                        <span className="font-bold text-sm leading-4">{CurrentSemester}</span>
                                    </div>
                                </div>
                                <div className="bg-white/20 backdrop-blur-sm rounded-xl px-3 py-1.5 text-white border border-white/10 shadow-sm flex items-center gap-1.5">
                                    <i className="fas fa-list-ul text-teal-200 text-xs"></i>
                                    <div>
                                        <span className="text-[8px] uppercase tracking-widest opacity-80 block leading-3">Exams</span>
                                        <span className="font-bold text-sm leading-4">{examSchedule.length}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Student info row */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3 pt-3 border-t border-white/20">
                            <div className="flex items-center gap-2">
                                <i className="fas fa-user-graduate text-teal-200 text-sm"></i>
                                <div>
                                    <p className="text-[8px] uppercase tracking-widest text-teal-100/70">Student</p>
                                    <p className="font-semibold text-white text-sm leading-5">{studentName}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <i className="fas fa-id-card text-teal-200 text-sm"></i>
                                <div>
                                    <p className="text-[8px] uppercase tracking-widest text-teal-100/70">Roll No.</p>
                                    <p className="font-semibold text-white text-sm leading-5">{rollNo}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <i className="fas fa-university text-teal-200 text-sm"></i>
                                <div>
                                    <p className="text-[8px] uppercase tracking-widest text-teal-100/70">Institution</p>
                                    <p className="font-semibold text-white text-xs leading-4">{institutionName}</p>
                                </div>
                            </div>
                        </div>

                        {/* Academic year badges */}
                        <div className="flex flex-wrap items-center gap-2 mt-3 pt-2 border-t border-white/10">
                            <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm text-white text-[10px] font-medium px-3 py-1 rounded-full border border-white/10">
                                <i className="fas fa-calendar-day text-[10px]"></i> {procYear}
                            </span>
                            <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm text-white text-[10px] font-medium px-3 py-1 rounded-full border border-white/10">
                                <i className="fas fa-book text-[10px]"></i> Sem {procSem}
                            </span>
                            <span className="inline-flex items-center gap-1.5 bg-emerald-400/20 backdrop-blur-sm text-emerald-100 text-[10px] font-medium px-3 py-1 rounded-full border border-emerald-300/20">
                                <i className="fas fa-check-circle text-[10px]"></i> {regularCount} Regular
                            </span>
                            <span className="inline-flex items-center gap-1.5 bg-rose-400/20 backdrop-blur-sm text-rose-100 text-[10px] font-medium px-3 py-1 rounded-full border border-rose-300/20">
                                <i className="fas fa-exclamation-triangle text-[10px]"></i> {arrearCount} Arrear
                            </span>
                        </div>
                    </div>
                </div>

                {/* ========== SCROLLABLE TIMETABLE ========== */}
                <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                    {semesters.map((sem, idx) => {
                        const semesterExams = sortedExams.filter(e => {
                            // Since API doesn't provide semester in examSchedule, 
                            // we'll use the current semester from main data
                            return true; // All exams belong to current semester
                        });
                        const hasArrear = semesterExams.some(e => e.isArrear);
                        const regInSem = semesterExams.filter(e => !e.isArrear).length;
                        const arrInSem = semesterExams.filter(e => e.isArrear).length;
                        const isCurrentSem = sem === CurrentSemester;

                        return (
                            <div key={idx} className="mb-6 last:mb-2">
                                {/* Semester Header */}
                                <div className={`flex items-center gap-3 mb-3 ${hasArrear ? 'border-l-4 border-rose-400 pl-3' : 'border-l-4 border-[#0d4f5c] pl-3'}`}>
                                    <div className="flex items-center gap-2.5">
                                        <span className={`${isCurrentSem ? 'bg-[#0d4f5c]' : 'bg-gray-600'} text-white text-sm font-bold px-4 py-1.5 rounded-full shadow-md flex items-center gap-2`}>
                                            Semester {sem}
                                            {isCurrentSem && (
                                                <span className="bg-white/20 text-white text-[8px] uppercase tracking-wider px-2 py-0.5 rounded-full">
                                                    Current
                                                </span>
                                            )}
                                        </span>
                                        <span className="text-xs text-gray-500 flex items-center gap-2">
                                            {regInSem > 0 && (<span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                                                <i className="fas fa-check-circle text-[10px]"></i> {regInSem} Reg
                                            </span>)}
                                            {arrInSem > 0 && (
                                                <span className="inline-flex items-center gap-1 bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full">
                                                    <i className="fas fa-exclamation-circle text-[10px]"></i> {arrInSem} Arr
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex-1 h-px bg-gradient-to-r from-gray-300/60 to-transparent"></div>
                                </div>

                                {/* Exams for this semester */}
                                <div className="grid grid-cols-1 gap-3.5">
                                    {semesterExams.map((exam, idx) => {
                                        const isArrear = exam.isArrear || false;
                                        const cardBg = isArrear
                                            ? 'bg-gradient-to-br from-rose-50/90 to-white border-rose-200/60 shadow-rose-100/30'
                                            : 'bg-gradient-to-br from-teal-50/60 to-white border-teal-100/50 shadow-teal-100/20';
                                        const borderAccent = isArrear ? 'border-l-8 border-l-rose-500' : 'border-l-8 border-l-[#0d4f5c]';
                                        const badgeType = isArrear ? 'badge-arrear' : 'badge-regular';
                                        const typeLabel = isArrear ? 'Arrear' : 'Regular';
                                        const iconType = isArrear ? 'fa-exclamation-circle' : 'fa-check-circle';

                                        return (
                                            <div
                                                key={idx}
                                                className={`relative rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${cardBg} ${borderAccent} overflow-hidden border border-white/40`}
                                            >
                                                {isArrear && (
                                                    <div className="absolute top-0 right-0 w-24 h-24 bg-rose-200/20 rounded-full blur-2xl -translate-y-1/2 translate-x-1/3"></div>
                                                )}
                                                <div className="relative px-5 py-4 md:px-6 md:py-5 flex flex-col md:flex-row md:items-center gap-3">
                                                    {/* Semester Badge */}
                                                    <div className="flex items-center gap-3 md:w-32 shrink-0">
                                                        <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full font-extrabold text-sm border-2 shadow-sm ${isArrear ? 'bg-rose-50 text-rose-600 border-rose-300' : 'bg-[#0d4f5c]/10 text-[#0d4f5c] border-[#0d4f5c]/30'}`}>
                                                            S{exam.Semester || sem}
                                                        </span>
                                                        <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full ${badgeType} shadow-sm`}>
                                                            <i className={`fas ${iconType} text-[10px]`}></i> {typeLabel}
                                                        </span>
                                                    </div>

                                                    {/* date & day */}
                                                    <div className="flex items-center gap-3 md:w-52 shrink-0">
                                                        <div className="bg-white/70 backdrop-blur p-2 rounded-xl border border-white/50 shadow-sm text-center min-w-[64px]">
                                                            <span className="block text-sm font-bold text-[#0d4f5c]">{formatDate(exam.examDate)}</span>
                                                        </div>
                                                        <span className="inline-flex items-center gap-1.5 bg-white/60 backdrop-blur px-3 py-1.5 rounded-full text-xs font-medium text-[#0d4f5c] border border-white/60 shadow-sm">
                                                            <i className="far fa-calendar-alt text-[10px]"></i> {exam.examDay || 'TBD'}
                                                        </span>
                                                    </div>

                                                    {/* subject */}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-mono font-bold text-[#0d4f5c] bg-white/60 px-3 py-1 rounded-lg text-sm border border-white/60 shadow-sm">
                                                                {exam.subjectCode}
                                                            </span>
                                                            <span className="font-semibold text-gray-800 text-sm md:text-base truncate">{exam.subjectName}</span>
                                                        </div>
                                                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                                            <span className="inline-flex items-center gap-1">
                                                                <i className="far fa-clock text-[10px]"></i> {getSessionLabel(exam.examSession)}
                                                            </span>
                                                            <span className="inline-flex items-center gap-1">
                                                                <i className="fas fa-tag text-[10px]"></i> {exam.examType === 'R' ? 'Regular' : exam.examType === 'A' ? 'Arrear' : exam.examType}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* time */}
                                                    <div className="md:w-52 shrink-0 flex justify-end">
                                                        <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-sm border border-white/60 shadow-sm px-4 py-2 rounded-xl time-pill">
                                                            <i className="fas fa-hourglass-half text-[#1a7a8a] text-sm"></i>
                                                            <span className="font-semibold text-[#0b3b45] text-sm">
                                                                {getTimeDisplay(exam)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* ========== FOOTER ========== */}
                <div className="flex-shrink-0 mt-4 flex flex-wrap items-center justify-between gap-3 bg-white/80 backdrop-blur-sm rounded-xl px-5 py-3 border border-gray-200/60 shadow-md">
                    <div className="flex items-center gap-3 text-xs text-gray-600">
                        <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-[#0d4f5c]"></span>
                            <span>Regular</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-rose-500"></span>
                            <span>Arrear</span>
                        </div>
                        <span className="text-gray-300">|</span>
                        <span className="flex items-center gap-1"><i className="far fa-clock text-[#1a7a8a] text-[10px]"></i> {examSchedule.length} exams</span>
                        <span className="text-gray-300">|</span>
                        <span className="flex items-center gap-1"><i className="fas fa-layer-group text-[#1a7a8a] text-[10px]"></i> {semesters.length} Semester{semesters.length > 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-slate-100/70 px-3 py-1.5 rounded-full border border-slate-200/50">
                        <i className="fas fa-info-circle text-[#0d4f5c] text-[10px]"></i>
                        <span>Report 15 min before</span>
                    </div>
                </div>

                <div className="flex-shrink-0 mt-2 text-center text-[10px] text-gray-400 flex items-center justify-center gap-1.5">
                    <i className="fas fa-magic text-[#1a7a8a] text-[10px]"></i>
                    <span>Marvelous UI • Semester {CurrentSemester} (Current)</span>
                </div>
            </div>
        </div>
    );
};

export default ExamTimetable;